import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Input,
  Label,
  LoadingState,
  StatPill,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Users } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { listBuildings } from '../../../api/buildings.js';
import { listOrgUsers } from '../../../api/org.js';
import {
  addTeamMember,
  createTeam,
  deleteTeam,
  listTeamMembers,
  listTeams,
  removeTeamMember,
  updateTeam,
  type TeamSummary,
} from '../../../api/teams.js';
import { ApiError } from '../../../lib/api-client.js';

export function TeamsSettingsPage() {
  const queryClient = useQueryClient();
  const teamsQuery = useQuery({ queryKey: ['teams'], queryFn: listTeams });
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<TeamSummary | null>(null);

  const removeMutation = useMutation({
    mutationFn: deleteTeam,
    onSuccess: () => {
      toast.success('Team archived');
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not archive team'),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Teams</h2>
          <p className="text-sm text-fg-secondary">
            Group your maintenance staff so work orders route to the right people.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          New team
        </Button>
      </div>

      {teamsQuery.isLoading ? (
        <LoadingState rows={4} />
      ) : teamsQuery.isError ? (
        <ErrorState description="Could not load teams." />
      ) : !teamsQuery.data || teamsQuery.data.teams.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No teams yet"
          description="Create a team to assign work orders to a group of staff."
          action={
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              New team
            </Button>
          }
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {teamsQuery.data.teams.map((team) => (
            <Card key={team.id} className="hover:border-primary cursor-pointer" onClick={() => setEditing(team)}>
              <CardHeader>
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">{team.name}</CardTitle>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(`Archive team "${team.name}"?`)) {
                        removeMutation.mutate(team.id);
                      }
                    }}
                    className="rounded p-1 text-fg-muted hover:bg-surface-muted hover:text-danger"
                    aria-label="Archive team"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                {team.description ? (
                  <p className="mb-2 text-sm text-fg-secondary">{team.description}</p>
                ) : null}
                <div className="flex flex-wrap gap-1.5">
                  <StatPill value={team.memberCount} label={team.memberCount === 1 ? 'member' : 'members'} />
                  {team.leadCount > 0 ? (
                    <StatPill value={team.leadCount} label="lead" tone="primary" />
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateTeamDialog open={createOpen} onOpenChange={setCreateOpen} />
      <TeamDetailDialog team={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

function CreateTeamDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const buildingsQuery = useQuery({
    queryKey: ['buildings', 'team-create'],
    queryFn: () => listBuildings({ limit: 100 }),
    enabled: open,
  });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [buildingId, setBuildingId] = useState('');

  const createMutation = useMutation({
    mutationFn: () =>
      createTeam({
        name,
        description: description || null,
        buildingId: buildingId || null,
        members: [],
      }),
    onSuccess: () => {
      toast.success('Team created');
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
      onOpenChange(false);
      setName('');
      setDescription('');
      setBuildingId('');
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not create team'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New team</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            createMutation.mutate();
          }}
          className="space-y-3"
        >
          <div className="space-y-1">
            <Label htmlFor="t-name">Name</Label>
            <Input id="t-name" required value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="t-desc">Description</Label>
            <textarea
              id="t-desc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="t-building">Building (optional)</Label>
            <select
              id="t-building"
              value={buildingId}
              onChange={(e) => setBuildingId(e.target.value)}
              className="h-10 w-full rounded border border-border bg-surface px-3 text-sm"
            >
              <option value="">Org-wide team</option>
              {buildingsQuery.data?.buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name || createMutation.isPending}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TeamDetailDialog({ team, onClose }: { team: TeamSummary | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const open = team !== null;
  const id = team?.id;

  const membersQuery = useQuery({
    queryKey: ['team-members', id],
    queryFn: () => listTeamMembers(id!),
    enabled: Boolean(id),
  });

  const usersQuery = useQuery({
    queryKey: ['org-users-for-team'],
    queryFn: listOrgUsers,
    enabled: open,
  });

  const [name, setName] = useState(team?.name ?? '');
  const [description, setDescription] = useState(team?.description ?? '');
  const [pendingUserId, setPendingUserId] = useState('');

  const renameMutation = useMutation({
    mutationFn: () =>
      updateTeam(id!, {
        name,
        description: description || null,
      }),
    onSuccess: () => {
      toast.success('Team updated');
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not update team'),
  });

  const addMutation = useMutation({
    mutationFn: () => addTeamMember(id!, { userId: pendingUserId, isLead: false }),
    onSuccess: () => {
      toast.success('Member added');
      setPendingUserId('');
      void queryClient.invalidateQueries({ queryKey: ['team-members', id] });
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not add member'),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => removeTeamMember(id!, userId),
    onSuccess: () => {
      toast.success('Member removed');
      void queryClient.invalidateQueries({ queryKey: ['team-members', id] });
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not remove member'),
  });

  if (!team) return null;

  const membersById = new Set((membersQuery.data?.members ?? []).map((m) => m.userId));
  const eligibleUsers = (usersQuery.data?.users ?? []).filter(
    (u) => !membersById.has(u.id) && !u.isSuperAdmin,
  );

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit team</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="td-name">Name</Label>
            <Input id="td-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="td-desc">Description</Label>
            <textarea
              id="td-desc"
              rows={2}
              value={description ?? ''}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
            />
          </div>
          <Button
            size="sm"
            onClick={() => renameMutation.mutate()}
            disabled={renameMutation.isPending}
          >
            Save changes
          </Button>

          <hr className="border-border" />

          <div>
            <Label className="mb-2 block">Members</Label>
            {(membersQuery.data?.members ?? []).length === 0 ? (
              <p className="text-sm text-fg-muted">No members yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {membersQuery.data!.members.map((m) => (
                  <li
                    key={m.id}
                    className="flex items-center justify-between rounded border border-border bg-surface px-3 py-2 text-sm"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{m.user.name}</span>
                      <span className="text-xs text-fg-muted">{m.user.email}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeMutation.mutate(m.userId)}
                      className="rounded p-1 text-fg-muted hover:bg-surface-muted hover:text-danger"
                      aria-label="Remove member"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3 flex items-end gap-2">
              <div className="flex-1 space-y-1">
                <Label htmlFor="td-add">Add member</Label>
                <select
                  id="td-add"
                  value={pendingUserId}
                  onChange={(e) => setPendingUserId(e.target.value)}
                  className="h-10 w-full rounded border border-border bg-surface px-3 text-sm"
                >
                  <option value="">Select a user</option>
                  {eligibleUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} — {u.email}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                size="sm"
                onClick={() => addMutation.mutate()}
                disabled={!pendingUserId || addMutation.isPending}
              >
                Add
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
